<script setup lang="ts">
import { InputAutoCompletion } from '@sancare/typescript-commons'
import {
  useAutoCompletionDebouncedWatcher
} from '@sancare/typescript-commons/src/misc/composables/useAutoCompletionDebouncedWatcher'
import { capitalize, lowerFirst } from '@sancare/typescript-commons/src/misc/string-utils'
import { FilterDataValue, FilterEvent } from '@sancare/typescript-commons/src/types/filters'
import { computed, ComputedRef, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import VueSlider from 'vue-slider-component'
import { useStore } from 'vuex'

import { FilterData } from '@/store/modules/filters/types'

const OFFSET_LABRESULT_VALUE_RANGE = 10

interface BiologyCompletionItem {
  code: string
  title: string
  type: 'lab_result' | 'categorical_lab_result'
  min?: number | string
  max?: number | string
}

interface BiologyRangeResult {
  min: string | number
  max: string
  list: Array<string | number>
}

const props = withDefaults(defineProps<{
  filterData: FilterData
  initialValue?: FilterDataValue | null
  isLegacy?: boolean
}>(), {
  initialValue: null,
  isLegacy: true
})

const emit = defineEmits<{
  'add-filter': [payload: FilterEvent]
}>()

const store = useStore()
const { t } = useI18n()

function initializeSubFiltersValues(): Record<string, string> {
  const values: Record<string, string> = {}

  if (props.filterData.subFilters) {
    props.filterData.subFilters.forEach((filter) => {
      if (filter.default) {
        values[filter.name] = filter.default
      } else if (filter.options) {
        const firstOption = filter.options[0]
        values[filter.name] = typeof firstOption === 'string' ? firstOption : firstOption.value
      } else {
        values[filter.name] = ''
      }
    })
  }

  return values
}

const showCompletion = ref(false)
const subFiltersValues = ref(initializeSubFiltersValues())
const selectedItem = ref<BiologyCompletionItem | null>(null)
const typedInput = ref('')
const inputMode = ref<'slider' | 'manual'>('slider')
const sliderValue = ref<[string | number, string | number]>([0, 0])
const labResultValue = ref<[string | number, string | number]>([0, 0])
const categoricalLabResultValue = ref('')

const completionItems: ComputedRef<BiologyCompletionItem[]> = computed(() => store.state.remoteLists.completionItems)
const biologyValues: ComputedRef<string[]> = computed(() => store.state.remoteLists.biologyValues)
const completionRequest: ComputedRef<{ fetching: boolean }> = computed(() => store.state.remoteLists.completionRequest)

function calculateRange(rawMin: string, rawMax: string): BiologyRangeResult {
  const newRawMin = rawMin.startsWith('<') || rawMin.startsWith('>') ? Number(rawMin.slice(1)) : Number(rawMin)
  const newRawMax = rawMax.startsWith('>') || rawMax.startsWith('<') ? Number(rawMax.slice(1)) : Number(rawMax)

  let min = newRawMin - OFFSET_LABRESULT_VALUE_RANGE * newRawMin / 100
  let max = newRawMax + OFFSET_LABRESULT_VALUE_RANGE * newRawMax / 100

  min = parseInt(String(min + 0.5))
  max = parseInt(String(max + 0.5))

  min -= min % 10
  max += 10 - (max % 10)

  if (min < 0 && newRawMin >= 0) {
    min = 0
  }

  const diff = max - min
  let step = 1

  if (diff > 1000) {
    step = 50
  } else if (diff > 100) {
    step = 5
  } else if (diff < 1 && diff > 0.1) {
    step = 0.05
  } else if (diff < 1 && diff > 0.01) {
    step = 0.005
  }

  let i = min
  const list = []
  if (min !== 0 && newRawMin >= 0) {
    list.push(`<${min}`)
  }
  while (i < max) {
    list.push(i)
    i += step
  }
  list.push(max)
  list.push(`>${max}`)

  if (min !== 0 && newRawMin >= 0) {
    return { min: `<${min}`, max: `>${max}`, list }
  } else {
    return { min, max: `>${max}`, list }
  }
}

const range = computed((): BiologyRangeResult => {
  if (!selectedItem.value) {
    return { min: 0, max: '>0', list: [0, '>0'] }
  }


  return calculateRange(String(selectedItem.value.min), String(selectedItem.value.max))
})

function parseInitialValue(value: string) {
  const [code, minOrValue, max] = value.split('\x1F')

  const type: 'lab_result' | 'categorical_lab_result' = max !== undefined
    ? 'lab_result'
    : 'categorical_lab_result'

  let newMin: number | string | undefined
  let newMax: number | string | undefined
  if (max === undefined) {
    newMin = undefined
    newMax = undefined
  } else {
    newMin = minOrValue.startsWith('<') || minOrValue.startsWith('>') ? minOrValue : Number(minOrValue)
    newMax = max.startsWith('>') || max.startsWith('<') ? max : Number(max)
  }

  return {
    code,
    type,
    min: newMin,
    max: newMax,
    value: minOrValue,
  }
}

async function getCompletionItemFromCode(code: string) {
  await fetchCompletionItems(code)
  return completionItems.value.find((item) => item.code === code)
}

async function initializeFromInitialValue(): Promise<void> {
  if (!props.initialValue) return

  const { value, ...parsedValue } = parseInitialValue(props.initialValue.value)

  const completionItem = await getCompletionItemFromCode(parsedValue.code)

  typedInput.value = completionItem?.title ?? ''

  selectedItem.value = completionItem

  if (parsedValue.max !== undefined) {
    const rangeData = calculateRange(
      String(completionItem?.min ?? 0),
      String(completionItem?.max ?? 0)
    )

    sliderValue.value = [
      parsedValue.min,
      parsedValue.max
    ]

    labResultValue.value = [
      parsedValue.min,
      parsedValue.max
    ]
  } else {
    categoricalLabResultValue.value = value

    if (completionItem) {
      await store.dispatch('remoteLists/getBiologyValues', {
        url: '/api/stay-search-criteria/list',
        urlParams: { title: completionItem.title }
      })
    }
  }

  if (props.initialValue.subFilters) {
    subFiltersValues.value = { ...props.initialValue.subFilters }
  }
}

function selectItem(completionItem: BiologyCompletionItem): void {
  store.commit('remoteLists/clearBiologyValues')
  selectedItem.value = completionItem
  typedInput.value = completionItem.title

  if (completionItem.type === 'lab_result') {
    const rawMin = completionItem.min ?? 0
    const rawMax = completionItem.max ?? 0
    const rangeData = calculateRange(String(rawMin), String(rawMax))

    sliderValue.value = [rangeData.min, rangeData.max]
    labResultValue.value = [rangeData.min, rangeData.max]
  } else {
    store.dispatch('remoteLists/getBiologyValues', {
      url: '/api/stay-search-criteria/list',
      urlParams: { title: completionItem.title }
    })
    categoricalLabResultValue.value = ''
  }

  closeCompletion()
}

function addFilter(): void {
  if (!selectedItem.value) return

  const name = props.filterData.name
  let type = 'biologyResult'
  let value = selectedItem.value.code

  type = type.replace('{filterType}', capitalize(props.filterData.name))
  if (props.filterData.subFilters) {
    props.filterData.subFilters.forEach((filter) => {
      type = type.replace(`{${filter.name}}`, capitalize(subFiltersValues.value[filter.name]))
    })
  }
  type = lowerFirst(type)

  if (selectedItem.value.type === 'lab_result') {
    value += `\x1F${labResultValue.value[0]}\x1F${labResultValue.value[1]}`
  } else {
    value += `\x1F${categoricalLabResultValue.value}`
  }

  emit('add-filter', { name, type, value })
  typedInput.value = ''
  categoricalLabResultValue.value = ''
  selectedItem.value = null
  store.commit('remoteLists/clearBiologyValues')
}

function openCompletion(): void {
  showCompletion.value = true
}

function closeCompletion(): void {
  showCompletion.value = false
}

function toggleInputMode(): void {
  inputMode.value = inputMode.value === 'slider' ? 'manual' : 'slider'
}

async function fetchCompletionItems(search: string, signal?: AbortSignal) {
  await store.dispatch('remoteLists/getCompletionItems', {
    url: '/api/stay-search-criteria/completion',
    urlParams: {
      type: props.filterData.name.toLowerCase(),
      search,
    },
    signal,
  })
}

function clearCompletionItems(): void {
  store.commit('remoteLists/clearCompletionItems')
  typedInput.value = ''
}

useAutoCompletionDebouncedWatcher(
  typedInput,
  completionItems,
  (search, signal) => fetchCompletionItems(search, signal),
  () => clearCompletionItems()
)

watch(sliderValue, (val) => {
  labResultValue.value = val
})

watch(
  () => props.initialValue,
  () => initializeFromInitialValue(),
  { immediate: true }
)
</script>

<template>
  <div class="d-flex flex-column gap-2">
    <div class="row align-items-center">
      <div
        v-on-click-outside="closeCompletion"
        class="autocompletion-container col"
        @keydown.esc="closeCompletion"
      >
        <input
          :id="`biology-filter-search`"
          v-model="typedInput"
          class="form-control"
          @click="openCompletion"
        >
        <input-auto-completion
          v-show="showCompletion && typedInput !== ''"
          :completion-list="completionItems"
          :is-loading="completionRequest.fetching"
          :title-prop="'code'"
          :description-prop="'title'"
          @select-item="selectItem"
        />
      </div>
    </div>
    <div
      v-if="selectedItem && selectedItem.type === 'lab_result'"
      class="row align-items-center"
      :class="inputMode === 'slider' ? 'mt-5' : 'mt-3'"
    >
      <div class="col">
        <vue-slider
          v-if="inputMode === 'slider'"
          v-model="sliderValue"
          :v-data="range.list"
          :tooltip="'always'"
          range
          class="mx-3"
        />
        <div
          v-else
          class="row align-items-center"
        >
          <div class="col-auto text-center">
            {{ t('biologyFilter.rangeFrom') }}
          </div>
          <div class="col">
            <input
              :id="`biology-filter-range-min`"
              v-model="labResultValue[0]"
              type="text"
              class="form-control"
            >
          </div>
          <div class="col-auto text-center">
            {{ t('biologyFilter.rangeTo') }}
          </div>
          <div class="col">
            <input
              :id="`biology-filter-range-max`"
              v-model="labResultValue[1]"
              type="text"
              class="col form-control"
            >
          </div>
        </div>
      </div>
      <div class="col-auto">
        <button
          :id="`biology-filter-add`"
          class="btn btn-primary"
          type="button"
          @click="addFilter"
        >
          {{ t('biologyFilter.ok') }}
        </button>
      </div>
    </div>
    <div
      v-if="selectedItem && selectedItem.type === 'categorical_lab_result'"
      class="input-group"
      :class="isLegacy ? ['col-5'] : ['col-12']"
    >
      <select
        :id="`biology-filter-select`"
        v-model="categoricalLabResultValue"
        class="form-select"
      >
        <option
          value=""
          disabled
        >
          {{ t('biologyFilter.selectValue') }}
        </option>
        <option
          v-for="(value, idx) in biologyValues"
          :key="'bio_value_'+idx"
          :value="value"
        >
          {{ value }}
        </option>
      </select>
      <div class="input-group-append">
        <button
          :id="`biology-filter-add`"
          class="btn btn-primary"
          type="button"
          @click="addFilter"
        >
          {{ t('biologyFilter.ok') }}
        </button>
      </div>
    </div>
  </div>
  <div
    v-if="selectedItem && selectedItem.type === 'lab_result'"
    class="row"
  >
    <div class="col">
      <span
        :id="`biology-filter-toggle-mode`"
        class="btn btn-link"
        @click="toggleInputMode()"
      >
        {{ inputMode === 'slider' ? t('biologyFilter.manualMode') : t('biologyFilter.normalMode') }}
      </span>
    </div>
  </div>
</template>
